import { ApiErrorSchema, type ApiError as ApiErrorPayload } from '@hrms/contracts/common';

import { env } from './env.js';

import { getTenantSlug } from '@/stores/auth.store';

/**
 * Thrown when the server responds non-2xx with a valid `ApiError` envelope.
 * For transport failures / invalid JSON we throw a raw `Error` (caller can
 * distinguish with `instanceof ApiError`).
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly correlationId: string;
  public readonly details: unknown;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.code;
    this.correlationId = payload.correlationId;
    this.details = payload.details;
  }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  readonly headers?: Record<string, string>;
  readonly signal?: AbortSignal;
}

/**
 * Token provider registered at app startup to avoid circular imports.
 * The auth store calls `registerTokenProvider` once it's initialised.
 */
let tokenProvider: (() => string | null) | null = null;
let tokenRefresher: (() => Promise<string | null>) | null = null;
let onUnauthenticated: (() => void) | null = null;
// Singleton: reuse an in-flight refresh so concurrent 401s don't each rotate the token.
let refreshInFlight: Promise<string | null> | null = null;

export function registerTokenProvider(fn: () => string | null): void {
  tokenProvider = fn;
}

export function registerTokenRefresher(fn: () => Promise<string | null>): void {
  tokenRefresher = fn;
}

export function registerUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

function buildUrl(path: string): string {
  const base = env.VITE_API_URL.replace(/\/+$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const withApi = normalized.startsWith('/api/') ? normalized : `/api${normalized}`;
  return `${base}${withApi}`;
}

function newCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `cid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The authenticated fetch every call shares: correlation id, bearer token,
 * tenant slug, JSON or multipart body, and one token refresh on a 401.
 */
async function send(
  method: HttpMethod,
  path: string,
  body: unknown,
  opts?: RequestOptions,
): Promise<Response> {
  const correlationId = newCorrelationId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlationId,
    ...opts?.headers,
  };

  const token = tokenProvider?.();
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const slug = getTenantSlug();
  if (slug && !headers['X-Tenant-Slug']) {
    headers['X-Tenant-Slug'] = slug;
  }

  const init: RequestInit = { method, headers };
  if (opts?.signal) init.signal = opts.signal;

  if (body !== undefined && method !== 'GET') {
    if (body instanceof FormData) {
      // Deliberately NO Content-Type. multipart/form-data carries a boundary
      // token that the browser generates when it serialises the FormData —
      // setting the header by hand omits it, and the server then parses zero
      // parts and reports "no file was uploaded" for a request that plainly
      // had one. The one place where saying less is required.
      init.body = body;
    } else {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
  }

  let response = await fetch(buildUrl(path), init);

  // On 401, attempt a token refresh — using a singleton so concurrent 401s share one refresh.
  if (response.status === 401 && tokenRefresher && !opts?.headers?.['Authorization']) {
    refreshInFlight ??= tokenRefresher()
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
    const newToken = await refreshInFlight;
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(buildUrl(path), { ...init, headers });
    } else {
      onUnauthenticated?.();
    }
  }
  return response;
}

/** Turn a failed response into the same errors `request` throws. */
async function throwForResponse(response: Response): Promise<never> {
  const rawText = await response.text();
  const parsedJson: unknown = rawText.length > 0 ? safeJsonParse(rawText) : undefined;
  if (response.status === 401) {
    onUnauthenticated?.();
  }
  const errorParse = ApiErrorSchema.safeParse(parsedJson);
  if (errorParse.success) {
    throw new ApiError(response.status, errorParse.data);
  }
  // Friendly messages for well-known status codes when the body is non-standard
  const fallbackMessages: Record<number, string> = {
    413: 'That file is too large.',
    429: 'Too many requests — please wait a moment and try again.',
    503: 'Service temporarily unavailable — please try again shortly.',
    502: 'Server is starting up — please try again in a few seconds.',
  };
  const bodyMessage = (() => {
    if (typeof parsedJson !== 'object' || parsedJson === null) return undefined;
    const msg = (parsedJson as Record<string, unknown>)['message'];
    return typeof msg === 'string' ? msg : undefined;
  })();
  throw new Error(
    bodyMessage ??
      fallbackMessages[response.status] ??
      `HTTP ${String(response.status)} — ${response.statusText}`,
  );
}

async function request<TRes>(
  method: HttpMethod,
  path: string,
  body: unknown,
  resSchema: { parse(data: unknown): TRes },
  opts?: RequestOptions,
): Promise<TRes> {
  const response = await send(method, path, body, opts);
  if (!response.ok) return throwForResponse(response);

  const rawText = await response.text();
  const parsedJson: unknown = rawText.length > 0 ? safeJsonParse(rawText) : undefined;
  return resSchema.parse(parsedJson);
}

/**
 * A binary download that needs the caller's login (a zip of client files).
 * A plain link cannot carry the bearer token, so the bytes are fetched here
 * and handed to the browser as a blob.
 */
async function requestBlob(
  path: string,
  opts?: RequestOptions,
): Promise<{ blob: Blob; filename: string | null }> {
  const response = await send('GET', path, undefined, opts);
  if (!response.ok) return throwForResponse(response);
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] !== undefined ? decodeURIComponent(match[1]) : null;
  return { blob: await response.blob(), filename };
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export const apiClient = {
  getBlob(path: string, opts?: RequestOptions): Promise<{ blob: Blob; filename: string | null }> {
    return requestBlob(path, opts);
  },
  get<TRes>(
    path: string,
    schema: { parse(data: unknown): TRes },
    opts?: RequestOptions,
  ): Promise<TRes> {
    return request('GET', path, undefined, schema, opts);
  },
  /**
   * Multipart upload. Separate from `post` because the body is FormData and
   * must not be JSON-serialised — and because the retry-after-401 path applies
   * equally, so this cannot bypass `request` and call fetch directly.
   */
  postForm<TRes>(
    path: string,
    form: FormData,
    schema: { parse(data: unknown): TRes },
    opts?: RequestOptions,
  ): Promise<TRes> {
    return request('POST', path, form, schema, opts);
  },
  post<TBody, TRes>(
    path: string,
    body: TBody,
    resSchema: { parse(data: unknown): TRes },
    opts?: RequestOptions,
  ): Promise<TRes> {
    return request('POST', path, body, resSchema, opts);
  },
  put<TBody, TRes>(
    path: string,
    body: TBody,
    resSchema: { parse(data: unknown): TRes },
    opts?: RequestOptions,
  ): Promise<TRes> {
    return request('PUT', path, body, resSchema, opts);
  },
  patch<TBody, TRes>(
    path: string,
    body: TBody,
    resSchema: { parse(data: unknown): TRes },
    opts?: RequestOptions,
  ): Promise<TRes> {
    return request('PATCH', path, body, resSchema, opts);
  },
  delete<TRes>(
    path: string,
    schema: { parse(data: unknown): TRes },
    opts?: RequestOptions,
  ): Promise<TRes> {
    return request('DELETE', path, undefined, schema, opts);
  },
} as const;
